import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNestedForm } from '@hooks/useNestedForm';
import { Plus, X } from 'lucide-react';
import { fetchJson } from '@utils/fetchJson';
import FieldInfoIcon from './FieldInfoIcon';
import { formatLabel } from '@utils/utils';
import DatePicker from 'react-datepicker';
import { Switch } from '@radix-ui/react-switch';
import TextEditor from '@components/form-elements/TextEditor';
import SmartSelect from './smartSelect';
import SmartSelectInput from './smartSelectInput';
import { storeRegistry } from '@components/moduleRegistry';
import { useCustomLayoutsStore } from '@plugin/customFieldsBuilder/store/CustomLayoutBuilderStore';
import Input from '@components/ui/Input';
const PreBuildFormNew = ({
    menuId,
    __depth = 0,
    getDefApi,
    directSchema = null,
    dynamicFields = [],
    saveApi,
    updateApi,
    primaryKey = 'id',
    editId = null,
    onClose,
    onReset,
    onSave,
    title = 'Add Item',
    sidebarSize = 'md',
    fieldOverrides = {},
    layout = [],
    skipFields = [],
    validationRules = {},
    labels = {},
    infoIcons = {},
    formData = {},
    setFormData,
    customModules = {},
    showcustomFields=false,
    canEdit=true,
    }) => {
  // Guard against infinite nesting
    const {
        showForm: nestedTarget,
        openForm,
        closeForm,
        canNest,
        nextDepth
    } = useNestedForm(2, __depth);

    const [definitions, setDefinitions] = useState([]);
    //const [formData, setFormData] = useState(formDataPre);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMode, setSubmitMode] = useState(null); // 'save' or 'saveNew'
    const { openCustomLayoutCreate } = useCustomLayoutsStore();
    const [rules, setRules] = useState(validationRules);
    //const [isDirty, setIsDirty] = useState(false);
    const formDataInitialized = useRef(false);
    useEffect(() => {
        if(directSchema != null){
            setDefinitions(directSchema);
        }
        console.log("update",validationRules);
    },[directSchema]);//getDefApi, directSchema, dynamicFields
    useEffect(() => {
      
  setRules(validationRules);
}, [validationRules]);
  
const handleInputChange = async (field, value, opts = {}) => {
  const override = fieldOverrides[field] || {};
  const isDisabled = computeDisabled(override, formData);

  // Block manual changes, but allow programmatic updates via { force: true }
  if (isDisabled && !opts.force) return;

  setFormData(prev => ({ ...prev, [field]: value }));
  setErrors(prev => ({ ...prev, [field]: '' }));

  const trigger = override?.onChangeTrigger;
  if (trigger && value === trigger.value) {
    if (trigger.action === 'callStoreMethod') {
      try {
        const storeFn = storeRegistry[trigger.storeKey]?.[trigger.method];
        if (typeof storeFn === 'function') {
          const payload = typeof trigger.payload === 'function'
            ? trigger.payload(formData, value)
            : trigger.payload || {};
          storeFn(payload);
        } else {
          console.warn(`Method '${trigger.method}' not found in store key '${trigger.storeKey}'`);
        }
      } catch (err) {
        console.error(`Failed to call method ${trigger.method} from store ${trigger.storeKey}`, err);
      }
    }
  }
};
const setFieldValue = (field, value) => handleInputChange(field, value, { force: true });
  // --- DISABLE HELPERS (add below evaluateShowWhen) ---
const evaluateDisabledWhen = (condition, formData) => {
  if (!condition) return false; // default: not disabled
  // Reuse the same logic/schema as showWhen
  return evaluateShowWhen(condition, formData) === true;
};

const computeDisabled = (override, formData) => {
  if (!override) return false;

  // 1) hard boolean
  if (override.disabled === true) return true;

  // 2) function(formData) -> boolean
  if (typeof override.disabled === 'function') {
    try { return !!override.disabled(formData); } catch { return false; }
  }

  // 3) conditional object (same shape as showWhen)
  if (override.disabledWhen) {
    return !!evaluateDisabledWhen(override.disabledWhen, formData);
  }

  return false;
};
// --- END DISABLE HELPERS ---
// --- NUMERIC HELPERS ---
const isNumericField = (override = {}, fieldDef = {}) => {
  const t = String(override.type || fieldDef?.Type || '').toLowerCase();
  // Treat "number" or "decimal" (or explicit hint) as numeric
  return t === 'number' || t === 'decimal' || override.input === 'number';
};

const getNumberConfig = (override = {}) => ({
  decimal: override.decimal ?? true,            // allow decimal point?
  allowNegative: override.allowNegative ?? false, // allow '-'
  min: override.min ?? undefined,               // numeric min (optional)
  max: override.max ?? undefined,               // numeric max (optional)
  step: override.step ?? (override.decimal ? 'any' : 1),
  precision: override.precision ?? undefined,   // e.g., 2 => 12.34
});

// caret utils
const hasSelection = (input) => input && (input.selectionStart !== input.selectionEnd);

const allowNumericKey = (e, { decimal, allowNegative }) => {
  const key = e.key;

  // Always allow control/navigation keys
  if (
    key === 'Backspace' || key === 'Delete' || key === 'Tab' ||
    key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End'
  ) return true;

  // Allow copy/paste/select all with Ctrl/Cmd
  if ((e.ctrlKey || e.metaKey) && ['a','c','v','x','z','y'].includes(key.toLowerCase())) {
    return true;
  }

  // Digits
  if (key >= '0' && key <= '9') return true;

  // Decimal point
  if (decimal && key === '.') return true;

  // Minus sign
  if (allowNegative && key === '-') return true;

  return false;
};

const sanitizeNumericOnChange = (raw, { decimal, allowNegative }) => {
  let s = String(raw);

  // Keep only digits, one dot, one optional leading minus
  // Remove invalid chars
  s = s.replace(/[^\d\.\-]/g, '');

  // Handle minus
  if (!allowNegative) {
    s = s.replace(/-/g, '');
  } else {
    // only one minus at start
    s = s.replace(/(?!^)-/g, '');
  }

  // Handle decimal
  if (!decimal) {
    s = s.replace(/\./g, '');
  } else {
    // keep only first dot
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    }
  }

  return s;
};

const clampAndFormatOnBlur = (s, { min, max, precision }) => {
  if (s === '' || s === '-' || s === '.' || s === '-.') return '';

  let num = Number(s);
  if (Number.isNaN(num)) return '';

  if (typeof min === 'number') num = Math.max(min, num);
  if (typeof max === 'number') num = Math.min(max, num);

  if (typeof precision === 'number') {
    num = Number(num.toFixed(precision));
  }

  return String(num);
};
// --- END NUMERIC HELPERS ---
const handleSubmit = async (resetAfter = false) => {
  if (!validateForm()) return;

  setIsSubmitting(true);
  setSubmitMode(resetAfter ? 'saveNew' : 'save');

  const url = editId ? updateApi : saveApi;
  const method = editId ? 'POST' : 'PUT';

  try {
    const res = await fetchJson(url, {
      method,
      body: JSON.stringify(formData)
    });

    if (res?.flag === 'S') {
      toast.success('Saved successfully');
      onSave?.();
      if (resetAfter){ 
        console.log('calling onReset?', typeof onReset);
        onReset?.({});
        
        setErrors({});     // ← clear validation
        
      } else onClose?.({ last_insert_id: res.lastID ?? res.last_insert_id });
    } else {
      toast.error(`Error: ${res.msg}`);
    }
  } catch (err) {
    toast.error('Something went wrong while saving.');
  } finally {
    setIsSubmitting(false);
    setSubmitMode(null);
  }
};

  const sizeClass = {
    sm: 'w-full md:w-[300px]',
    md: 'w-full md:w-[500px]',
    lg: 'w-full md:w-[700px]',
    xl: 'w-full md:w-[900px]'
  }[sidebarSize];

    // const validateField = (field, val, rule) => {
    //     const labelText = labels[field] || field;
    //     if (rule.required && (!val || val.length === 0)) return `${labelText} is required.`;
    //     if (rule.type === 'email' && val && !/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(val)) return 'Invalid email format.';
    //     if (rule.regex && val && !new RegExp(rule.regex).test(val)) return 'Invalid format.';
    //     if (rule.maxLength && val?.length > rule.maxLength) return `Max length ${rule.maxLength} exceeded.`;
    //     if (rule.min && val < rule.min) return `Minimum value is ${rule.min}.`;
    //     if (rule.max && val > rule.max) return `Maximum value is ${rule.max}.`;
    //     return '';
    // };
    const normalizeRule = (rulePart) =>
  typeof rulePart === 'object' && rulePart !== null
    ? rulePart
    : { value: rulePart, message: undefined };

const validateField = (field, val, rule) => {
  const labelText = labels[field] || field;

  // normalize all supported rules
  const {
    required,
    type,
    pattern,   // preferred
    regex,     // backward-compat alias
    minLength,
    maxLength,
    min,
    max,
  } = rule || {};

  const patternRule = pattern ?? regex;

  // REQUIRED
  if (required) {
    const { value, message } = normalizeRule(required);
    if (value && (!val || (typeof val === 'string' && val.trim().length === 0))) {
      return message || `${labelText} is required.`;
    }
  }

  // TYPE: email
  if (type) {
    const { value, message } = normalizeRule(type);
    if (value === 'email') {
  const checkVal = (val ?? "").toString().trim();
  const emailRe = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z]{2,})+$/;

  if (!emailRe.test(checkVal)) {
    return message || "Invalid email address.";
  }
}
  }

  // PATTERN / REGEX
  //if (patternRule && val) {
  // if (patternRule) {
  //   const { value, message } = normalizeRule(patternRule);
  //   const re =
  //     value instanceof RegExp
  //       ? value
  //       : new RegExp(value); // allow string patterns too
  //   if (!re.test(val)) return message || 'Invalid format.';
  // }
  if (patternRule) {
  const { value, message } = normalizeRule(patternRule);
  const re = value instanceof RegExp ? value : new RegExp(value);

  const checkVal = (val ?? "").toString().trim();

  // FIX: do NOT validate pattern when field is empty and not required
  if (checkVal === "") return "";

  if (!re.test(checkVal)) {
    return message || "Invalid format.";
  }
}

  // MIN LENGTH
  if (minLength && val != null) {
    const { value, message } = normalizeRule(minLength);
    if (typeof val === 'string' && val.length < value) {
      return message || `Minimum length is ${value}.`;
    }
  }

  // MAX LENGTH
  if (maxLength && val != null) {
    const { value, message } = normalizeRule(maxLength);
    if (typeof val === 'string' && val.length > value) {
      return message || `Max length ${value} exceeded.`;
    }
  }

  // MIN (numbers)
  if (min && val != null && val !== '') {
    const { value, message } = normalizeRule(min);
    if (+val < value) return message || `Minimum value is ${value}.`;
  }

  // MAX (numbers)
  if (max && val != null && val !== '') {
    const { value, message } = normalizeRule(max);
    if (+val > value) return message || `Maximum value is ${value}.`;
  }

  return '';
};
// const isVisible = (fieldName) => {
//   // Find the field config inside layout > row[]
//   let fieldConfig;
//   for (const section of layout) {
//     const match = section.row.find(item => item.field === fieldName);
//     if (match) {
//       fieldConfig = match;
//       break;
//     }
//   }
//   const condition = fieldConfig?.showWhen;
//   return evaluateShowWhen(condition, formData);
// };
const isVisible = (fieldName) => {
  let condition;
  for (const section of layout) {
    if (section.type === 'customSplit') {
      const left = section.left?.find(i => i.field === fieldName);
      const right = section.right?.find(i => i.field === fieldName);
      condition = (left || right)?.showWhen;
    } else {
      const match = section.row?.find(i => i.field === fieldName);
      condition = match?.showWhen;
    }
    if (condition !== undefined) break;
  }
  return evaluateShowWhen(condition, formData);
};
    // const validateForm = () => {
    //     const newErrors = {};
    //     for (const field in validationRules) {
    //         if (!isVisible(field)) continue;
    //     const err = validateField(field, formData[field], validationRules[field]);
    //     if (err) newErrors[field] = err;
    //     }
    //     setErrors(newErrors);
    //     return Object.keys(newErrors).length === 0;
    // };
    const validateForm = () => {
  const newErrors = {};
  for (const field in rules) {
    if (!isVisible(field)) continue;

    // Skip validation for disabled fields
    const override = fieldOverrides[field] || {};
    if (computeDisabled(override, formData)) continue;

    const err = validateField(field, formData[field], rules[field]);
    if (err) newErrors[field] = err;
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};
// const evaluateShowWhen = (condition, formData) => {
//   if (!condition) return true;

//   if (Array.isArray(condition)) {
//     console.warn("Invalid showWhen: condition is array", condition);
//     return true;
//   }

//   if (condition.hasOwnProperty('and')) {
//     const result = condition.and.every(c => {
//       const match = formData[c.field] === c.value;
//       return match;
//     });
//     return result;
//   }

//   if (condition.hasOwnProperty('or')) {
//     const result = condition.or.some(c => {
//       const match = formData[c.field] === c.value;
//       return match;
//     });
//     return result;
//   }

//   if (condition.hasOwnProperty('field') && condition.hasOwnProperty('value')) {
//     const match = formData[condition.field] === condition.value;
//     return match;
//   }

//   return true;
// };
const evaluateShowWhen = (condition, formData) => {
  if (!condition) return true;

  // Helper to normalize empty values
  const normalize = (v) =>
    v === "" || v === undefined ? null : v;

  // Extract value once
  const fieldVal = condition.field ? normalize(formData[condition.field]) : null;

  // -----------------------------
  // 1. AND LOGIC
  // -----------------------------
  if (Array.isArray(condition.and)) {
    return condition.and.every(c => evaluateShowWhen(c, formData));
  }

  // -----------------------------
  // 2. OR LOGIC
  // -----------------------------
  if (Array.isArray(condition.or)) {
    return condition.or.some(c => evaluateShowWhen(c, formData));
  }

  // -----------------------------
  // 3. isNull / isNotNull
  // -----------------------------
  if (condition.field && condition.isNull === true) {
    return fieldVal === null;
  }

  if (condition.field && condition.isNotNull === true) {
    return fieldVal !== null;
  }

  // -----------------------------
  // 4. Operator Based
  // -----------------------------
  const operator = condition.operator || "==";
  const expected = normalize(condition.value);

  switch (operator) {
    case "==":
    case "=":
      return fieldVal === expected;

    case "!=":
    case "<>":
      return fieldVal !== expected;

    case ">":
      return Number(fieldVal) > Number(expected);

    case "<":
      return Number(fieldVal) < Number(expected);

    case ">=":
      return Number(fieldVal) >= Number(expected);

    case "<=":
      return Number(fieldVal) <= Number(expected);

    case "in":
      return Array.isArray(expected) && expected.includes(fieldVal);

    case "notIn":
      return Array.isArray(expected) && !expected.includes(fieldVal);

    default:
      console.warn("Unknown operator:", operator, condition);
      return true;
  }
};



const parseMaybeNumber = (v) => {
  if (v === '') return null;                 // treat placeholder as null
  const n = Number(v);
  return Number.isNaN(n) ? v : n;            // "3" -> 3, "A3" stays "A3"
};


    // const toggle = (val) => {
    //   const updated = selected.includes(val)
    //     ? selected.filter((v) => v !== val)
    //     : [...selected, val];
    //   handleInputChange(Field, updated.join(','));
    // };
    const same = (a, b) => String(a) === String(b);

// ensure we always work with an array
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  if (typeof v === 'string') {
    // JSON array?
    try { const parsed = JSON.parse(v); if (Array.isArray(parsed)) return parsed; } catch {}
    // CSV string
    return v.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [v];
};

   const renderField = (fieldDef, visibilityCondition = null) => {
  if (!fieldDef) return <div className="flex-1 min-w-[200px]" />;
  const { Field } = fieldDef;
  //if (skipFields.includes(Field)) return null;

  const override = fieldOverrides[Field] || {};
  const condition = override.showWhen || visibilityCondition;
  const visible = evaluateShowWhen(condition, formData);
  if (!visible) return null;

  const disabled = computeDisabled(override, formData);   // ← NEW
  const fieldValue = formData[Field];
  const labelText = labels[Field] || override.label || Field;
  const info = infoIcons[Field];

  const labelBlock = (
    <label className="text-xs font-regular mb-0.5 text-gray-700 flex items-center gap-1">
      {formatLabel(labelText)}
      {info?.show && <FieldInfoIcon text={info.text} />}{}
      {rules[Field]?.required && <span className="text-red-500">*</span>}
    </label>
  );

  // smartSelectInput
  if (override.type === 'smartSelectInput') {
    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <SmartSelectInput
          id={Field}
          label=""
          value={fieldValue}
          disabled={disabled}
          onObjectSelect={() => {}}
          onSelect={(value) => { value === "" ? handleInputChange(Field, null) : handleInputChange(Field, value) }}
          config={override.config || {}}
        />
        {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
      </div>
    );
  }

  // smartSelect
  if (override.type === 'smartSelect') {
    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <SmartSelect
          id={Field}
          label=""
          value={fieldValue}
          disabled={disabled}
          onSelect={(val, item) => {
            if (!item) {
              handleInputChange(Field, null);
            } else {
              handleInputChange(Field, { value: val, label: item.categoryName });
            }
          }}
          config={override.config || {}}
        />
        {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
      </div>
    );
  }

  // editor
  if (override.type === 'editor') {
    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <TextEditor
          showEditorInitially={override?.showEditorInitially}
          value={fieldValue || ''}
          onChange={(val) => handleInputChange(Field, val)}
          readOnly={disabled}
          disabled={disabled}
        />
      </div>
    );
  }

  // toggle
  if (override.type === 'toggle') {
    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <Switch
          checked={!!fieldValue}
          onCheckedChange={(val) => handleInputChange(Field, val)}
          disabled={disabled}
        />
      </div>
    );
  }
  if (override.type === 'password') {
    return (
      <div className="flex flex-col w-full">
        {labelBlock}
       <Input label={""} type='password'
       value={fieldValue || ''}
          onChange={(e) => handleInputChange(Field, e.target.value)}
      />
      {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
      </div>
    );
  }

  // date
  if (override.type === 'date') {
    const today = new Date();
    const selectedDate = fieldValue ? new Date(fieldValue) : (override.defaultToday ? today : null);
    let minDate = override.minDate ? new Date(override.minDate) : null;
    let maxDate = override.maxDate ? new Date(override.maxDate) : null;

    if (override.preventPast) minDate = today;
    if (override.preventFuture) maxDate = today;

    if (override.preventBefore && !minDate) minDate = new Date(override.preventBefore);
    if (override.preventAfter && !maxDate) maxDate = new Date(override.preventAfter);

    if (override.limitTo === 'month') {
      minDate = new Date(today.getFullYear(), today.getMonth(), 1);
      maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (override.limitTo === 'week') {
      const day = today.getDay();
      const diffToMonday = today.getDate() - day + (day === 0 ? -6 : 1);
      minDate = new Date(today.setDate(diffToMonday));
      maxDate = new Date(minDate); maxDate.setDate(minDate.getDate() + 6);
    } else if (override.limitTo === 'year') {
      minDate = new Date(today.getFullYear(), 0, 1);
      maxDate = new Date(today.getFullYear(), 11, 31);
    }

    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <DatePicker
          selected={selectedDate}
          onChange={(date) => handleInputChange(Field, date?.toISOString())}
          className="ws-date form-input w-full text-gray-600 bg-gray-100 rounded focus:outline-none px-3 py-2 pr-10 px-2 py-1 text-sm"
          placeholderText="Select a date"
          dateFormat="yyyy-MM-dd"
          minDate={minDate}
          showMonthDropdown={override?.showMonthDropdown}
          showYearDropdown={override?.showYearDropdown}
          maxDate={maxDate}
          disabled={disabled}
        />
        {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
      </div>
    );
  }

  // dropdown
  if (override.type === 'dropdown') {
    return (
      <div className="flex flex-col w-full">
        {labelBlock}
       <select
  className="ws-select form-input w-full text-gray-600 bg-gray-100 rounded focus:outline-none px-3 py-2 pr-10 px-2 py-1 text-sm"
  value={fieldValue == null ? '' : String(fieldValue)}
  onChange={(e) => handleInputChange(Field, e.target.value)}  // '' when cleared
  disabled={disabled}
>
  <option value="">-- Select --</option>
  {override.options?.map((opt) => (
    <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
  ))}
</select>

        {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
      </div>
    );
  }

  // radio
  if (override.type === 'radio') {
    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <div className='flex flex-wrap gap-2 items-center min-h-[40px]'>
          {override.options?.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm">
             <input
  type="radio"
  name={Field}
  value={String(opt.value)}
  checked={same(fieldValue, opt.value)}
  onChange={() => handleInputChange(Field, opt.value)}
  disabled={disabled}
/>
{opt.label}
            </label>
          ))}
        </div>
        {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
      </div>
    );
  }

  // checkbox
  const keyOf = (v) => String(typeof v === 'object' ? (v.id ?? v.value ?? v.label) : v);

// currentValue can be "", "a,b", ["a","b"], etc.
const toArrFromCsv = (v) => {
  if (Array.isArray(v)) return v.map(String);
  if (v == null || v === '') return [];
  return String(v).split(',').map(s => s.trim()).filter(Boolean);
};

// turn array back into CSV (empty => "")
const toCsv = (arr) => (arr.length ? arr.join(',') : '');


  if (override.type === 'CheckboxList' || override.type === 'checkbox') {
    // toggle using handleInputChange (no setState needed)
    const toggleCsv = (fieldKey, currentValue, val, isChecked) => {
    const curr = toArrFromCsv(currentValue);
    const key  = String(val);

    let next;
    if (isChecked) {
      next = curr.includes(key) ? curr : [...curr, key];
    } else {
      next = curr.filter(v => v !== key);
    }
    handleInputChange(fieldKey, toCsv(next));
  };

//     const toggle = (val) => {
//   setSelected((prev = []) => {
//     const key = String(val);
//     const has = prev.map(String).includes(key);
//     return has ? prev.filter(v => String(v) !== key) : [...prev, val];
//   });
// };

    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <div className='flex flex-wrap gap-2 items-center min-h-[40px]'>
          {override.options?.map((opt) => (
            <label key={opt.value} className="flex w-auto items-center gap-2 text-sm">
            <input
                type="checkbox"
                value={String(opt.value)}
                checked={toArrFromCsv(fieldValue).includes(keyOf(opt.value))}
                onChange={(e) => toggleCsv(Field, fieldValue, opt.value, e.target.checked)}
                className="ws-checkbox"
                disabled={disabled}
              />
              {opt.label}
            </label>
          ))}
        </div>
        {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
      </div>
    );
  }
  const isNumeric = isNumericField(override, fieldDef);
  if (isNumeric) {
    const cfg = getNumberConfig(override);
    const localVal = fieldValue ?? ''; // keep as string for editing

    return (
      <div className="flex flex-col w-full">
        {labelBlock}
        <input
          type="text"                       // keep text for precise control
          inputMode={cfg.decimal ? 'decimal' : 'numeric'}
          pattern={cfg.decimal ? '[0-9]*[.,]?[0-9]*' : '[0-9]*'}
          value={localVal}
          disabled={disabled}
          readOnly={disabled}
          className="ws-input form-input w-full text-gray-600 text-md bg-gray-100 rounded focus:outline-none text-sm px-3 py-2 pr-10 rounded"
          onKeyDown={(e) => {
            if (!allowNumericKey(e, cfg)) {
              e.preventDefault();
              return;
            }
            // prevent multiple '-' or '-' not at start
            if (cfg.allowNegative && e.key === '-') {
              const el = e.currentTarget;
              if (el.selectionStart !== 0 || (el.value.includes('-') && !hasSelection(el))) {
                e.preventDefault();
              }
            }
            // prevent second '.' if not selecting over existing
            if (cfg.decimal && e.key === '.') {
              const el = e.currentTarget;
              if (el.value.includes('.') && !hasSelection(el)) {
                e.preventDefault();
              }
            }
          }}
          onChange={(e) => {
            const cleaned = sanitizeNumericOnChange(e.target.value, cfg);
            handleInputChange(Field, cleaned);
          }}
          onBlur={(e) => {
            const clamped = clampAndFormatOnBlur(e.target.value, cfg);
            handleInputChange(Field, clamped);
          }}
        />
        {errors[Field] && (
          <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>
        )}
      </div>
    );
  }
  // default input
  return (
    <div className="flex flex-col w-full">
      {labelBlock}
      <input
        type={override.type || 'text'}
        value={fieldValue || ''}
        onChange={(e) => handleInputChange(Field, e.target.value)}
        className="ws-input form-input w-full text-gray-600 text-md bg-gray-100 rounded focus:outline-none text-sm px-3 py-2 pr-10 rounded"
        disabled={disabled}
        readOnly={disabled}
      />
      {errors[Field] && <div className="text-xs text-red-500 mt-1">{errors[Field]}</div>}
    </div>
  );
};

  return (
    <>
      <div className={`modal-panel size-${sidebarSize} fixed top-[50px] right-0 h-[calc(100vh-56px)] bg-white shadow-lg z-50 flex flex-col w-screen max-w-full md:${sizeClass}`}>
        <div className="sticky top-0 z-10 bg-gray-50 border-b p-3 shadow-sm flex justify-between items-center">
          <h2 className="text-lg text-gray-700 font-regular pl-3">{title}</h2>
          <button onClick={onClose} className="absolute -left-3 rounded-full bg-white w-[28px] h-[28px] shadow-lg flex items-center justify-center">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 pr-6">
            {layout.map((row, i) => {
                if (row.type === 'customSplit') {
                    return (
                        <div key={`customSplit-${i}`} className="flex flex-col md:flex-row gap-6">
                        {/* LEFT COLUMN */}
                        <div className="md:w-2/3 flex flex-col gap-4">
                        {row.left.map((item, j) => {
                            if (item.addcustomFieldBtn) {
                                if (!menuId) return null;
                                return (
                                <button
                                    key={`btn-${i}`}
                                    onClick={() =>
                                    openCustomLayoutCreate({ payload: { menuId: menuId } })
                                    }
                                    className="p-1.5 pl-3 mt-3 pr-3 bg-white border border-blue-500 border-dashed max-w-[250px] w-[200px] text-primary text-sm hover:bg-blue-50 rounded flex items-center"
                                >
                                    <Plus className="inline mr-2" size={16} />
                                    Create Custom Fields
                                </button>
                                );
                            }
                            // const fieldKey = item.field || item.custom || `left-${j}`;
                            // return (
                            // <div key={fieldKey}>
                            //     {item.field && renderField(definitions.find(d => d.Field === item.field), item.showWhen)}
                            //     {item.custom && typeof customModules[item.custom] === 'function' && (customModules[item.custom]())}
                            // </div>
                            // );
                            const fieldKey = item.field || item.custom || `left-${j}`;
                            // const visible = !item.showWhen || formData[item.showWhen.field] === item.showWhen.value;
                            // if (!visible) return null;
                            const visible = evaluateShowWhen(item.showWhen, formData);
                            if (!visible) return null;
                            
                            return (
                            <div key={fieldKey}>
                                {item.field && renderField(definitions.find(d => d.Field === item.field))}
                                {item.custom && typeof customModules[item.custom] === 'function' && (customModules[item.custom]())}
                            </div>
                            );
                        })}
                        </div>
                        {/* RIGHT COLUMN */}
                        <div className="md:w-1/3 flex flex-col gap-4">
                            {row.right.map((item, j) => {
                                if (item.addcustomFieldBtn) {
                                    if (!menuId) return null;
                                return (
                                <button
                                    key={`btn-${i}`}
                                    onClick={() =>
                                    openCustomLayoutCreate({ payload: { menuId: menuId } })
                                    }
                                    className="p-1.5 pl-3 mt-3 pr-3 bg-white border border-blue-500 border-dashed max-w-[250px] w-[200px] text-primary text-sm hover:bg-blue-50 rounded flex items-center"
                                >
                                    <Plus className="inline mr-2" size={16} />
                                    Create Custom Fields
                                </button>
                                );
                            }
                            
                                const fieldKey = item.field || `right-${j}`;
                                // const visible = !item.showWhen || formData[item.showWhen.field] === item.showWhen.value;
                                // if (!visible) return null;
                                 const visible = evaluateShowWhen(item.showWhen, formData);
                            if (!visible) return null;
                                    return (
                                    <div key={fieldKey}>
                                        {item.field && renderField(definitions.find(d => d.Field === item.field), item.showWhen)}
                                        {item.custom && typeof customModules[item.custom] === 'function' && (customModules[item.custom]())}
                                    </div>
                                    );
                                    
                                })}
                        </div>
                        </div>
                    );
                }
                //const groupVisible = !row.showWhen || formData[row.showWhen.field] === row.showWhen.value;
                const groupVisible = evaluateShowWhen(row.showWhen, formData);
                if (!groupVisible) return null;
                return (
                    <div className="flex flex-wrap gap-4" key={row.label || `row-${i}`}>
                    {row.row.map((col, j) => {
                    // Handle the "Create Custom Fields" button (preserves your existing logic)
                    if (col.addcustomFieldBtn) {
                      if (!menuId) return null;
                      return (
                        showcustomFields && (
                          <button
                            key={`btn-${i}`}
                            onClick={() => openCustomLayoutCreate({ payload: { menuId } })}
                            className="p-1.5 pl-3 mt-3 pr-3 bg-white border border-blue-500 border-dashed text-primary max-w-[250px] w-[200px] text-sm hover:bg-blue-50 rounded flex items-center"
                          >
                            <Plus className="inline mr-2" size={16} />
                            Create Custom Fields
                          </button>
                        )
                      );
                    }

                    const fieldKey = col?.field || col?.custom || `blank-${j}`;

                    // NEW: decide visibility BEFORE rendering wrapper
                    // If col has showWhen, respect it; otherwise let renderField handle any override.showWhen
                    const colVisible = evaluateShowWhen(col?.showWhen, formData);

                    // If column is not visible (per layout), SKIP the wrapper entirely
                    if (!colVisible) return null;

                    // Determine if this column will actually render anything
                    const hasRenderable =
                      (col?.field && definitions.some(d => d.Field === col.field)) ||
                      (col?.custom && typeof customModules[col.custom] === 'function');

                    if (!hasRenderable) return null; // no placeholder divs

                    return (
                      <div key={fieldKey} className={`${col?.colSize || 'flex-1 mb-6 min-w-[100px]'}`}>
                        {col?.field &&
                          renderField(
                            definitions.find(d => d.Field === col.field),
                            col.showWhen // pass through if you want renderField to also check override.showWhen
                          )}
                        {col?.custom && typeof customModules[col.custom] === 'function' && customModules[col.custom]()}
                      </div>
                    );
                  })}
                    </div>
                );
            })}
        </div>
        <div className="sticky bottom-0 shadow-lg bg-white z-8 border-t p-3 flex justify-end pr-5 gap-2">
            {/* <button onClick={() => handleSubmit(false)} className="bg-blue-600 text-white px-4 py-1 rounded">Save</button>
            <button onClick={() => handleSubmit(true)} className="border border-gray-300 px-4 py-1 rounded">Save & New</button> */}
           {canEdit && (
              <>
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={isSubmitting}
                  className="bg-blue-600 text-white px-4 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting && submitMode === 'save' ? 'Saving...' : 'Save'}
                </button>

                <button
                  onClick={() => handleSubmit(true)}
                  disabled={isSubmitting}
                  className="border border-gray-300 px-4 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting && submitMode === 'saveNew' ? 'Saving...' : 'Save & New'}
                </button>
              </>
            )}
        </div>
    </div>
    </>
  );
};
export default PreBuildFormNew;
